/**
 * Authentication Context
 * Manages user authentication state and provides auth methods
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authService } from "../services/authService";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  /**
   * Load user profile from backend
   * This auto-creates user on first login
   */
  const loadUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setAccessDenied(false);

      const userData = await authService.getMe();
      
      // Проверка: пользователь должен существовать в БД
      if (!userData || !userData.telegram_id) {
        setAccessDenied(true);
        setUser(null);
        setError("Доступ запрещен. Обратитесь к администратору.");
        return;
      }
      
      setUser(userData);
    } catch (err) {
      console.error("Failed to load user:", err);
      
      // Если 403 - доступ запрещен
      if (err.status === 403 || err.status === 401) {
        setAccessDenied(true);
        setError("Доступ запрещен. Вы не добавлены в систему.");
      } else {
        setError(err.message || "Failed to load user");
      }
      
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Refresh user data
   */
  const refreshUser = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  /**
   * Check if user has admin privileges
   */
  const isAdmin = useCallback(() => {
    return authService.isAdmin(user);
  }, [user]);

  /**
   * Check if user has super admin privileges
   */
  const isSuperAdmin = useCallback(() => {
    return authService.isSuperAdmin(user);
  }, [user]);

  /**
   * Check if user is a manager
   */
  const isManager = useCallback(() => {
    return authService.isManager(user);
  }, [user]);

  /**
   * Load user on mount
   */
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const value = {
    user,
    loading,
    error,
    accessDenied,
    refreshUser,
    isAdmin,
    isSuperAdmin,
    isManager,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Custom hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  
  return context;
}
